import { LiveChatWidget } from '@livechat/widget-react';
import { Fragment, Suspense, useMemo } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';
import { Loading } from '../components/common/Loading';
import { useAppUpdateCheck } from '../hooks/useAppUpdateCheck';
import { useOnChange } from '../hooks/useOnChange';
import useOnInitialMount from '../hooks/useOnInitialMount';
import { useParseParams } from '../hooks/useParseParams';
import { useGetUserAction } from '../middleware/useGetUserAction';
import { usePostHogInitiate } from '../middleware/usePostHogInitiate';
import { useQuestionByType } from '../middleware/useQuestionByType';
import { useRefreshToken } from '../middleware/useRefreshToken';
import { audioState } from '../states/audioState';
import { tokenState } from '../states/auth';
import { supportVisibilityState } from '../states/header';
import { modularFeedbacksState } from '../states/modularFeedbacks';
import { questionState, selectedCompanyState } from '../states/recording';
import { scoresState } from '../states/score';
import { uiState } from '../states/uiState';
import { NotFound } from '../views/NotFound';
import { authRoutes } from './configs/authRoutes';
import { mainRoutes } from './configs/mainRoutes';
import { onboardingRoutes } from './configs/onboardingRoutes';
import { APP_PATHS } from './paths';
import { createRoutes } from './routeFactory';

export function AppRoutes() {
  useParseParams();
  usePostHogInitiate();
  const { pathname } = useLocation();
  const resetScore = useSetRecoilState(scoresState);
  const resetModularFeedbacks = useSetRecoilState(modularFeedbacksState);
  const resetQuestion = useSetRecoilState(questionState);
  const resetAudioState = useResetRecoilState(audioState);
  const resetSelectedCompany = useResetRecoilState(selectedCompanyState);

  const navigate = useNavigate();
  const token = useRecoilValue(tokenState);
  const { mutate: refreshToken } = useRefreshToken();
  const getUserAction = useGetUserAction();
  const getCommonQuestions = useQuestionByType();
  const appUpdateCheckSnackBar = useAppUpdateCheck();

  const [supportVisibility, setSupportVisibility] = useRecoilState(
    supportVisibilityState
  );

  const isOnboardingCompleted = useMemo(() => {
    return getUserAction.data?.data.isCompleted ?? true;
  }, [getUserAction.data]);

  const [ui, setUi] = useRecoilState(uiState);

  const setMenuOpen = (menuOpen: boolean) => setUi({ ...ui, menuOpen });

  // Only check onboarding status when user has a token and not on auth routes
  const isAuthRoute =
    pathname.startsWith('/nace') ||
    pathname.startsWith('/enter-') ||
    pathname.startsWith('/create-') ||
    pathname.startsWith('/welcome');

  useOnInitialMount(() => {
    console.log(
      'Initial mount, pathname:',
      pathname,
      'isAuthRoute:',
      isAuthRoute
    );
    if (!isAuthRoute) {
      refreshToken({});
      getUserAction.mutate({ actionName: 'onboardingComplete' });
      getCommonQuestions.mutate({ questionType: 'common' });
      getCommonQuestions.mutate({ questionType: 'behavioral' });
      getCommonQuestions.mutate({ questionType: 'hypothetical' });
    }
  });

  // lets reset the score when the pathname changes
  useOnChange({
    value: pathname,
    defaultValue: '',
    onChange: () => {
      console.log('Pathname changed to:', pathname);
      resetScore(null);
      resetAudioState();
      resetModularFeedbacks(null);
      if (window.innerWidth <= 768) {
        setMenuOpen(false);
      }
      if (pathname === APP_PATHS.home) {
        resetSelectedCompany();
        resetQuestion(undefined);
      }
    },
  });

  useOnChange({
    value: isOnboardingCompleted,
    defaultValue: true,
    onChange: () => {
      console.log('Onboarding change detected:', {
        pathname,
        isAuthRoute,
        hasToken: !!token,
        isOnboardingCompleted,
        willNavigate: token && !isAuthRoute,
      });
      if (token && !isAuthRoute) {
        console.log('Navigating to onboarding from:', pathname);
        navigate(APP_PATHS.onboardingJobSearch);
      }
    },
  });

  return (
    <Fragment>
      {appUpdateCheckSnackBar}
      <Routes>
        <Route
          path="/"
          element={
            <Suspense fallback={<Loading />}>
              {token ? (
                <Navigate to={APP_PATHS.home} />
              ) : (
                <Navigate to={APP_PATHS.enterEmail} />
              )}
            </Suspense>
          }
        />
        {createRoutes(authRoutes)}
        {createRoutes(onboardingRoutes)}
        {createRoutes(mainRoutes)}
        <Route path={APP_PATHS.any} element={<NotFound />} />
      </Routes>
      <LiveChatWidget
        license="17154567"
        visibility={supportVisibility}
        onVisibilityChanged={({ visibility }) => {
          if (visibility === 'minimized') {
            setSupportVisibility('hidden');
          } else {
            setSupportVisibility(visibility);
          }
        }}
      />
    </Fragment>
  );
}
